import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="page-type-form"
export default class extends Controller {
  static targets = ["pageTypeSelect", "basicFields", "blogFields", "glossaryFields"]
  static values = { currentType: String }


  connect() {
    // Debug: mark connection
    try { console.log("[page-type-form] connect") } catch(_) {}
    // Initialize field visibility using the select value if present,
    // otherwise fall back to the currentType value passed from the server.
    const initialType = this.hasPageTypeSelectTarget
      ? this.pageTypeSelectTarget.value
      : (this.hasCurrentTypeValue ? this.currentTypeValue : "basic_page")
    this.showFields(initialType)
  }

  pageTypeChanged(event) {
    try { console.log("[page-type-form] pageTypeChanged", event?.target?.value) } catch(_) {}
    const selectedType = event?.target?.value || (this.hasPageTypeSelectTarget ? this.pageTypeSelectTarget.value : null)
    this.showFields(selectedType)
    this.validateRequiredFields()
  }

  showFields(type) {
    const selectedType = type || (this.hasPageTypeSelectTarget ? this.pageTypeSelectTarget.value : null) || (this.hasCurrentTypeValue ? this.currentTypeValue : "basic_page")

    // Hide all field groups
    if (this.hasBasicFieldsTarget) {
      this.basicFieldsTarget.classList.add("hidden")
    }
    if (this.hasBlogFieldsTarget) {
      this.blogFieldsTarget.classList.add("hidden")
    }
    if (this.hasGlossaryFieldsTarget) {
      this.glossaryFieldsTarget.classList.add("hidden")
    }

    // Show relevant field group
    switch(selectedType) {
      case "basic_page":
        if (this.hasBasicFieldsTarget) {
          this.basicFieldsTarget.classList.remove("hidden")
        }
        break
      case "blog_post":
        if (this.hasBlogFieldsTarget) {
          this.blogFieldsTarget.classList.remove("hidden")
        }
        break
      case "glossary_definition":
        if (this.hasGlossaryFieldsTarget) {
          this.glossaryFieldsTarget.classList.remove("hidden")
        }
        break
    }

    this.updateRequiredFields(selectedType)
  }

  updateRequiredFields(pageType) {
    // Remove all required attributes first
    this.element.querySelectorAll("[data-required-for]").forEach(field => {
      field.removeAttribute("required")
      const label = field.closest(".field")?.querySelector("label")
      if (label) {
        label.textContent = label.textContent.replace(" *", "")
      }
    })

    // Add required attributes for current page type
    this.element.querySelectorAll(`[data-required-for*="${pageType}"]`).forEach(field => {
      field.setAttribute("required", "required")
      const label = field.closest(".field")?.querySelector("label")
      if (label && !label.textContent.includes(" *")) {
        label.textContent = label.textContent + " *"
      }
    })
  }

  validateRequiredFields() {
    const selectedType = this.pageTypeSelectTarget.value
    let isValid = true
    const errors = []

    // Clear previous errors
    this.element.querySelectorAll(".field-error").forEach(error => error.remove())

    // Validate based on page type
    switch(selectedType) {
      case "blog_post":
        const publicationDate = this.element.querySelector("[name*='publication_date']")
        if (publicationDate && !publicationDate.value) {
          this.showFieldError(publicationDate, "Publication date is required for blog posts")
          isValid = false
        }
        break
      case "glossary_definition":
        const termName = this.element.querySelector("[name*='term_name']")
        if (termName && !termName.value) {
          this.showFieldError(termName, "Term name is required for glossary definitions")
          isValid = false
        }
        break
    }

    return isValid
  }

  showFieldError(field, message) {
    const errorEl = document.createElement("div")
    errorEl.className = "field-error text-red-600 text-sm mt-1"
    errorEl.textContent = message

    const fieldContainer = field.closest(".field")
    if (fieldContainer) {
      fieldContainer.appendChild(errorEl)
    } else {
      field.parentNode.appendChild(errorEl)
    }

    field.classList.add("border-red-500")
  }

  // Handle form submission validation
  submitForm(event) {
    if (!this.validateRequiredFields()) {
      event.preventDefault()
      event.stopPropagation()

      // Show general error message
      this.showGeneralError("Please correct the errors below before submitting.")

      // Focus on first error field
      const firstError = this.element.querySelector(".border-red-500")
      if (firstError) {
        firstError.focus()
        firstError.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }

  showGeneralError(message) {
    // Remove existing general error
    const existingError = this.element.querySelector(".general-error")
    if (existingError) {
      existingError.remove()
    }

    const errorEl = document.createElement("div")
    errorEl.className = "general-error bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"
    errorEl.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Form Error</h3>
          <div class="mt-2 text-sm text-red-700">${message}</div>
        </div>
      </div>
    `

    // Insert at the top of the form
    const form = this.element.querySelector("form")
    if (form) {
      form.insertBefore(errorEl, form.firstChild)
    }
  }

  // Handle field changes to clear errors
  clearFieldError(event) {
    const field = event.target
    field.classList.remove("border-red-500")

    const fieldContainer = field.closest(".field")
    if (fieldContainer) {
      const error = fieldContainer.querySelector(".field-error")
      if (error) {
        error.remove()
      }
    }

    // Clear general error if all fields are valid
    const hasErrors = this.element.querySelector(".border-red-500")
    if (!hasErrors) {
      const generalError = this.element.querySelector(".general-error")
      if (generalError) {
        generalError.remove()
      }
    }
  }
}
